import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useState } from 'react';
// ── Sidebar ───────────────────────────────────────────────────────────
/**
 * localStorage key under which the sidebar's collapsed state
 * persists. Public so consumers can reset / inspect it.
 */
export const SIDEBAR_COLLAPSED_STORAGE_KEY = 'osd.nav.sidebar.collapsed';
function readCollapsed(defaultCollapsed) {
    if (typeof window === 'undefined')
        return defaultCollapsed;
    const raw = window.localStorage.getItem(SIDEBAR_COLLAPSED_STORAGE_KEY);
    if (raw === null)
        return defaultCollapsed;
    return raw === 'true';
}
function writeCollapsed(collapsed) {
    if (typeof window === 'undefined')
        return;
    window.localStorage.setItem(SIDEBAR_COLLAPSED_STORAGE_KEY, collapsed ? 'true' : 'false');
}
/**
 * Sidebar — primary navigation surface. Renders each section as a
 * collapsible `<details>` group (`SidebarSection`). Uses native
 * `<nav>` + `<ul>` so it stays a11y-friendly without JS.
 *
 * Icon-rail mode (Linear-style): when `collapsed: true`, the
 * sidebar shrinks to ~3rem wide and hides section bodies + link
 * labels; section icons + an `aria-label` keep it usable. Hovering
 * or focusing the sidebar in collapsed mode temporarily expands it
 * (pure CSS — no state mutation) so keyboard / mouse users can
 * still see the labels. The persistent collapsed state is only
 * flipped when the user clicks the toggle button.
 *
 * @example
 *   <Sidebar sections={[
 *     { id: 'main', label: 'Main', children: [
 *       { to: '/', label: 'Home' },
 *       { to: '/about', label: 'About' },
 *     ] },
 *   ]} />
 */
export function Sidebar({ sections, ariaLabel = 'Primary', className, collapsed, defaultCollapsed = false, onCollapsedChange, }) {
    const isControlled = collapsed !== undefined;
    const [internalCollapsed, setInternalCollapsed] = useState(() => readCollapsed(defaultCollapsed));
    const effectiveCollapsed = isControlled ? collapsed : internalCollapsed;
    // Persist uncontrolled state to localStorage so the icon-rail
    // preference survives navigation / reloads.
    useEffect(() => {
        if (!isControlled)
            writeCollapsed(internalCollapsed);
    }, [isControlled, internalCollapsed]);
    const toggle = useCallback(() => {
        const next = !effectiveCollapsed;
        if (!isControlled)
            setInternalCollapsed(next);
        onCollapsedChange?.(next);
    }, [effectiveCollapsed, isControlled, onCollapsedChange]);
    const classes = ['osd-sidebar', effectiveCollapsed ? 'osd-sidebar--collapsed' : null, className]
        .filter(Boolean)
        .join(' ');
    return (_jsxs("nav", { className: classes, "aria-label": ariaLabel, children: [_jsx("button", { type: "button", className: "osd-sidebar__toggle", onClick: toggle, "aria-expanded": !effectiveCollapsed, "aria-label": effectiveCollapsed ? 'Expand sidebar' : 'Collapse sidebar', title: effectiveCollapsed ? 'Expand sidebar' : 'Collapse sidebar', children: _jsx("span", { className: "osd-sidebar__toggle-icon", "aria-hidden": "true", children: effectiveCollapsed ? '›' : '‹' }) }), _jsx("ul", { className: "osd-sidebar__list", children: sections.map((section) => (_jsx("li", { className: "osd-sidebar__item", children: _jsx(SidebarSection, { section: section, collapsed: effectiveCollapsed }) }, section.id))) })] }));
}
/**
 * localStorage key under which the section's INLINE-OPEN state
 * persists. Distinct from the legacy "always-open" state which is
 * implied by `!collapsed`. Public so consumers can reset / inspect.
 */
export const SIDEBAR_SECTION_STORAGE_PREFIX = 'osd.nav.';
function readOpen(sectionId, defaultOpen) {
    if (typeof window === 'undefined')
        return defaultOpen;
    const raw = window.localStorage.getItem(SIDEBAR_SECTION_STORAGE_PREFIX + sectionId + '.open');
    if (raw === null)
        return defaultOpen;
    return raw === 'true';
}
function writeOpen(sectionId, open) {
    if (typeof window === 'undefined')
        return;
    window.localStorage.setItem(SIDEBAR_SECTION_STORAGE_PREFIX + sectionId + '.open', open ? 'true' : 'false');
}
/**
 * SidebarSection — collapsible group inside a Sidebar.
 *
 * Two render modes driven by the parent `<Sidebar collapsed>` prop:
 *
 *   - Expanded mode (`collapsed=false`): native `<details>`/`<summary>`
 *     group. Clicking the summary toggles the body. Body shows full
 *     link list with icons + labels. Default-open for `main`,
 *     `admin`, `design` sections; default-closed otherwise.
 *     Open state is persisted to localStorage.
 *
 *   - Icon-rail mode (`collapsed=true`): the section becomes a
 *     clickable icon button. Clicking it toggles `inlineOpen` for
 *     JUST this section — the body drops down INSIDE the rail
 *     showing the link icons (labels appear on hover via CSS).
 *     Other sections stay collapsed. The header (whole-sidebar
 *     expand toggle) is in the parent Sidebar; this component owns
 *     per-section inline expansion.
 *
 * The collapsed-section behaviour fixes root/osd#125 + the live
 * feedback from 2026-09-22 ("the sidebar is still fucked. i want it
 * to collapse to main cat icons"). Hover/focus on the rail
 * expands the whole sidebar to full width (CSS-only); click a
 * section icon to pin its body open inline.
 */
export function SidebarSection({ section, collapsed = false }) {
    const defaultOpen = section.id === 'main' || section.id === 'admin' || section.id === 'design';
    const [open, setOpen] = useState(() => readOpen(section.id, defaultOpen));
    useEffect(() => {
        writeOpen(section.id, open);
    }, [section.id, open]);
    // In icon-rail mode the section is a clickable button (not a
    // <details> summary). Clicking it toggles `open` for just this
    // section; the body appears inline below the icon. The
    // <details>/<summary> machinery is overkill here because we
    // don't want the section to "navigate" via the summary click.
    if (collapsed) {
        return (_jsxs("div", { className: [
                'osd-sidebar-section',
                'osd-sidebar-section--collapsed',
                open ? 'osd-sidebar-section--inline-open' : null,
            ]
                .filter(Boolean)
                .join(' '), children: [_jsx("button", { type: "button", className: "osd-sidebar-section__rail-button", "aria-expanded": open, "aria-controls": `osd-sidebar-section-${section.id}-body`, "aria-label": section.label, title: section.label, onClick: () => setOpen((v) => !v), children: section.icon !== undefined ? (_jsx("span", { className: "osd-sidebar-section__icon", "aria-hidden": "true", children: section.icon })) : (_jsx("span", { className: "osd-sidebar-section__icon-fallback", "aria-hidden": "true", children: section.label.charAt(0) })) }), open ? (_jsx("ul", { id: `osd-sidebar-section-${section.id}-body`, className: "osd-sidebar-section__list osd-sidebar-section__list--inline", children: section.children.map((link) => (_jsx("li", { className: "osd-sidebar-section__item", children: _jsxs("a", { className: "osd-sidebar-link osd-sidebar-link--inline", href: link.to, title: link.label, children: [link.icon !== undefined ? (_jsx("span", { className: "osd-sidebar-link__icon", "aria-hidden": "true", children: link.icon })) : null, _jsx("span", { className: "osd-sidebar-link__label", children: link.label })] }) }, link.to))) })) : null] }));
    }
    // Expanded mode: native <details>. Summary click toggles the body.
    return (_jsxs("details", { className: "osd-sidebar-section", open: open, onToggle: (e) => {
            setOpen(e.currentTarget.open);
        }, children: [_jsxs("summary", { className: "osd-sidebar-section__summary", children: [section.icon !== undefined ? (_jsx("span", { className: "osd-sidebar-section__icon", "aria-hidden": "true", children: section.icon })) : null, _jsx("span", { className: "osd-sidebar-section__label", children: section.label })] }), _jsx("ul", { className: "osd-sidebar-section__list", children: section.children.map((link) => (_jsx("li", { className: "osd-sidebar-section__item", children: _jsxs("a", { className: "osd-sidebar-link", href: link.to, children: [link.icon !== undefined ? (_jsx("span", { className: "osd-sidebar-link__icon", "aria-hidden": "true", children: link.icon })) : null, _jsx("span", { className: "osd-sidebar-link__label", children: link.label })] }) }, link.to))) })] }));
}
/**
 * SidebarLink — single link inside a SidebarSection. Renders as a
 * plain `<a href>` so the design system stays router-agnostic.
 * Consumers using a client-side router should either pass a
 * router-shaped `<Link>` via children-pattern wrapping, or
 * duplicate this thin component locally with the router's `<Link>`
 * (the only thing this component adds is the `osd-sidebar-link`
 * BEM classes — easy to re-apply in a wrapper).
 *
 * @example
 *   <SidebarLink to="/users" active>Users</SidebarLink>
 */
export function SidebarLink({ to, active = false, className, children, ...rest }) {
    const classes = [
        'osd-sidebar-link',
        active === true ? 'osd-sidebar-link--active' : null,
        className,
    ]
        .filter(Boolean)
        .join(' ');
    return (_jsx("a", { href: to, className: classes, "aria-current": active === true ? 'page' : undefined, ...rest, children: children }));
}
//# sourceMappingURL=Nav.js.map